import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, userAPI } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(true);

  // A sessão vive em cookies httpOnly — validamos sempre via /auth/me ao
  // arrancar (o cookie pode ter expirado). O ``user`` em localStorage serve
  // apenas para pintar a UI sem flicker enquanto o /me não responde.
  useEffect(() => {
    authAPI.me()
      .then(res => {
        setUser(res.data);
        localStorage.setItem('user', JSON.stringify(res.data));
      })
      .catch(() => {
        localStorage.removeItem('user');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // `identifier` aceita email OU username — o backend procura por qualquer
  // um dos dois (case-insensitive no email). O backend devolve os cookies de
  // sessão (httpOnly) na resposta; aqui guardamos apenas o utilizador.
  const login = useCallback(async (identifier, password) => {
    const res = await authAPI.login(identifier, password);
    const { user: userData } = res.data;
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (_e) {
      // Mesmo que a chamada falhe, limpamos o estado local.
    }
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  // Recarrega o utilizador a partir do backend (ex.: após mudar a password,
  // para limpar a flag must_change_password).
  const refreshUser = useCallback(async () => {
    const res = await authAPI.me();
    setUser(res.data);
    localStorage.setItem('user', JSON.stringify(res.data));
    return res.data;
  }, []);

  const setLang = useCallback((newLang) => {
    if (!['pt', 'fr', 'en'].includes(newLang)) return;
    setUser(prev => {
      if (!prev) {
        localStorage.setItem('lang', newLang);
        return prev;
      }
      const updated = { ...prev, language: newLang };
      localStorage.setItem('user', JSON.stringify(updated));
      if (prev.id) {
        userAPI.update(prev.id, { language: newLang }).catch(() => {});
      }
      return updated;
    });
  }, []);

  const lang = user?.language || localStorage.getItem('lang') || 'pt';

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser, loading, lang, setLang }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
